import React, { useState } from "react"
import {Book, ChevronDown, ChevronUp, Code, Lightbulb, Target} from 'lucide-react'

export default function LearnSection() {
  return (
    <section className="py-20 bg-slate-900">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4">Master Dynamic Programming</h2>
          <p className="text-slate-400 text-lg">Complete guide to understanding DP algorithms and solving interview problems</p>
        </div>

        <div className="max-w-5xl mx-auto space-y-6">
          <ArticleCard
            title="What is Dynamic Programming? (Define Dynamic Programming)"
            icon={<Book className="w-6 h-6" />}
            content={
              <>
                <p className="mb-4">
                  <strong>Dynamic Programming (DP)</strong> is an algorithmic technique used to solve complex problems by breaking them down into simpler subproblems. The key idea is to store the results of subproblems to avoid redundant calculations, a concept known as <strong>memoization</strong>.
                </p>
                <p className="mb-4">
                  <strong>Dynamic programming explained</strong>: Think of DP as "smart recursion" - instead of solving the same subproblem multiple times, you solve it once and remember the answer. This transforms exponential time complexity into polynomial time.
                </p>
                <div className="bg-slate-950 p-4 rounded-lg border border-slate-700">
                  <h4 className="font-semibold text-cyan-400 mb-2">Two Approaches to Dynamic Coding:</h4>
                  <ul className="space-y-2 text-slate-300">
                    <li><strong>Top-Down (Memoization):</strong> Start with the original problem and recursively break it down, storing results</li>
                    <li><strong>Bottom-Up (Tabulation):</strong> Start with the smallest subproblems and build up to the solution using a DP table</li>
                  </ul>
                </div>
              </>
            }
          />

          <ArticleCard
            title="Dynamic Programming Algorithm: Core Principles"
            icon={<Target className="w-6 h-6" />}
            content={
              <>
                <p className="mb-4">
                  A <strong>dynamic algorithm</strong> must satisfy two key properties to be solvable with DP:
                </p>
                <div className="space-y-4">
                  <div className="bg-slate-950 p-4 rounded-lg border border-emerald-500/30">
                    <h4 className="font-semibold text-emerald-400 mb-2">1. Optimal Substructure</h4>
                    <p className="text-slate-300">An optimal solution to the problem contains optimal solutions to subproblems. This is the foundation of <strong>dynamic programming and optimal control</strong> theory.</p>
                  </div>
                  <div className="bg-slate-950 p-4 rounded-lg border border-cyan-500/30">
                    <h4 className="font-semibold text-cyan-400 mb-2">2. Overlapping Subproblems</h4>
                    <p className="text-slate-300">The same subproblems are solved multiple times during recursion. DP eliminates this redundancy by using a <strong>dynamic programming table</strong> to store solutions.</p>
                  </div>
                </div>
              </>
            }
          />

          <ArticleCard
            title="Dynamic Programming Example: Fibonacci Sequence"
            icon={<Code className="w-6 h-6" />}
            content={
              <>
                <p className="mb-4">
                  The Fibonacci sequence is the classic <strong>dynamic programming example</strong> that demonstrates the power of memoization:
                </p>
                <div className="bg-slate-950 p-4 rounded-lg border border-slate-700 mb-4">
                  <pre className="text-sm text-slate-300">
{`# Naive Recursion: O(2^n) - Exponential!
def fib(n):
    if n <= 1: return n
    return fib(n-1) + fib(n-2)  # Redundant calls

# Dynamic Programming Python: O(n) - Linear!
def fib_dp(n):
    dp = [0] * (n + 1)
    dp[1] = 1
    for i in range(2, n + 1):
        dp[i] = dp[i-1] + dp[i-2]  # Store results
    return dp[n]`}
                  </pre>
                </div>
                <p className="text-slate-300">
                  This example shows how <strong>dynamic programming python</strong> implementations can reduce time complexity from exponential to linear by using a DP table.
                </p>
              </>
            }
          />

          <ArticleCard
            title="LeetCode Dynamic Programming: Top Interview Problems"
            icon={<Lightbulb className="w-6 h-6" />}
            content={
              <>
                <p className="mb-4">
                  <strong>LeetCode dynamic programming</strong> questions are among the most common in coding interviews. Here are the patterns you must master for <strong>leetcode dp</strong> success:
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-slate-950 p-4 rounded-lg border border-slate-700">
                    <h4 className="font-semibold text-cyan-400 mb-2">1. Fibonacci Pattern</h4>
                    <ul className="text-sm text-slate-300 space-y-1">
                      <li>• Climbing Stairs (LC #70)</li>
                      <li>• House Robber (LC #198)</li>
                      <li>• Min Cost Climbing Stairs (LC #746)</li>
                    </ul>
                  </div>
                  <div className="bg-slate-950 p-4 rounded-lg border border-slate-700">
                    <h4 className="font-semibold text-emerald-400 mb-2">2. 0/1 Knapsack</h4>
                    <ul className="text-sm text-slate-300 space-y-1">
                      <li>• Partition Equal Subset Sum (LC #416)</li>
                      <li>• Target Sum (LC #494)</li>
                      <li>• Last Stone Weight II (LC #1049)</li>
                    </ul>
                  </div>
                  <div className="bg-slate-950 p-4 rounded-lg border border-slate-700">
                    <h4 className="font-semibold text-amber-400 mb-2">3. Unbounded Knapsack</h4>
                    <ul className="text-sm text-slate-300 space-y-1">
                      <li>• Coin Change (LC #322)</li>
                      <li>• Coin Change 2 (LC #518)</li>
                      <li>• Rod Cutting Problem</li>
                    </ul>
                  </div>
                  <div className="bg-slate-950 p-4 rounded-lg border border-slate-700">
                    <h4 className="font-semibold text-purple-400 mb-2">4. Longest Common Subsequence</h4>
                    <ul className="text-sm text-slate-300 space-y-1">
                      <li>• LCS (LC #1143)</li>
                      <li>• Edit Distance (LC #72)</li>
                      <li>• Longest Palindromic Subsequence (LC #516)</li>
                    </ul>
                  </div>
                </div>
              </>
            }
          />

          <ArticleCard
            title="Dynamic Programming Problem: The Knapsack Algorithm"
            icon={<Code className="w-6 h-6" />}
            content={
              <>
                <p className="mb-4">
                  The <strong>knapsack problem</strong> is the most important <strong>dynamic programming problem</strong> for interview preparation. Given items with weights and values, find the maximum value that fits in a knapsack of capacity W.
                </p>
                <div className="bg-slate-950 p-4 rounded-lg border border-slate-700 mb-4">
                  <h4 className="font-semibold text-cyan-400 mb-2">Dynamic Programming Table Approach:</h4>
                  <pre className="text-sm text-slate-300 mt-2">
{`# dp[i][w] = max value using first i items with capacity w
def knapsack(weights, values, W):
    n = len(weights)
    dp = [[0] * (W + 1) for _ in range(n + 1)]
    
    for i in range(1, n + 1):
        for w in range(W + 1):
            if weights[i-1] <= w:
                # Include or exclude item
                dp[i][w] = max(
                    values[i-1] + dp[i-1][w - weights[i-1]],
                    dp[i-1][w]
                )
            else:
                dp[i][w] = dp[i-1][w]
    
    return dp[n][W]  # Maximum value`}
                  </pre>
                </div>
                <p className="text-slate-300">
                  <strong>Time Complexity:</strong> O(n × W) | <strong>Space:</strong> O(n × W) - optimizable to O(W)
                </p>
              </>
            }
          />

          <ArticleCard
            title="Rod Cutting Problem: Classic DP Challenge"
            icon={<Target className="w-6 h-6" />}
            content={
              <>
                <p className="mb-4">
                  The <strong>rod cutting problem</strong> is a variation of the unbounded knapsack pattern. Given a rod of length n and prices for different lengths, determine the maximum profit by cutting the rod into pieces.
                </p>
                <div className="bg-slate-950 p-4 rounded-lg border border-slate-700 mb-4">
                  <pre className="text-sm text-slate-300">
{`def rod_cutting(prices, n):
    # dp[i] = max profit for rod of length i
    dp = [0] * (n + 1)
    
    for i in range(1, n + 1):
        for j in range(i):
            # Cut at position j+1
            dp[i] = max(dp[i], prices[j] + dp[i - j - 1])
    
    return dp[n]

# Example: prices = [1, 5, 8, 9, 10, 17, 17, 20]
# rod_cutting(prices, 4) = 10 (cut into 2+2)`}
                  </pre>
                </div>
                <p className="text-slate-300">
                  This problem demonstrates how <strong>dynamic programming</strong> can transform O(2^n) brute-force into O(n²) optimal solution.
                </p>
              </>
            }
          />

          <ArticleCard
            title="Dynamic Programming in Java: Implementation Tips"
            icon={<Code className="w-6 h-6" />}
            content={
              <>
                <p className="mb-4">
                  <strong>Dynamic programming in Java</strong> follows similar patterns but with syntax differences. Here's a complete Java implementation:
                </p>
                <div className="bg-slate-950 p-4 rounded-lg border border-slate-700 mb-4">
                  <pre className="text-sm text-slate-300">
{`public class DynamicProgramming {
    // Fibonacci with DP
    public static int fibonacci(int n) {
        int[] dp = new int[n + 1];
        dp[0] = 0;
        dp[1] = 1;
        
        for (int i = 2; i <= n; i++) {
            dp[i] = dp[i-1] + dp[i-2];
        }
        return dp[n];
    }
    
    // Coin Change Problem
    public static int coinChange(int[] coins, int amount) {
        int[] dp = new int[amount + 1];
        Arrays.fill(dp, amount + 1);
        dp[0] = 0;
        
        for (int i = 1; i <= amount; i++) {
            for (int coin : coins) {
                if (i >= coin) {
                    dp[i] = Math.min(dp[i], dp[i - coin] + 1);
                }
            }
        }
        return dp[amount] > amount ? -1 : dp[amount];
    }
}`}
                  </pre>
                </div>
                <div className="bg-amber-500/10 border border-amber-500/30 p-4 rounded-lg">
                  <p className="text-amber-300 font-semibold mb-2">Java DP Best Practices:</p>
                  <ul className="text-slate-300 space-y-1 text-sm">
                    <li>• Use <code>Arrays.fill()</code> for initialization</li>
                    <li>• Prefer <code>int[]</code> over <code>Integer[]</code> for performance</li>
                    <li>• Consider space optimization (rolling array technique)</li>
                  </ul>
                </div>
              </>
            }
          />

          <ArticleCard
            title="How to Identify a Dynamic Programming Problem"
            icon={<Lightbulb className="w-6 h-6" />}
            content={
              <>
                <p className="mb-4">
                  Not sure if a problem requires DP? Look for these signals in any <strong>dynamic programming problem</strong>:
                </p>
                <div className="space-y-3">
                  <div className="flex gap-3 items-start">
                    <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-cyan-400 font-bold">1</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">Problem asks for "maximum/minimum/longest/shortest"</h4>
                      <p className="text-slate-400 text-sm">Optimization problems are prime DP candidates (max profit, min cost, longest subsequence)</p>
                    </div>
                  </div>
                  <div className="flex gap-3 items-start">
                    <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-emerald-400 font-bold">2</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">Problem asks to "count the number of ways"</h4>
                      <p className="text-slate-400 text-sm">Counting problems often have overlapping subproblems (climbing stairs, coin combinations)</p>
                    </div>
                  </div>
                  <div className="flex gap-3 items-start">
                    <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-amber-400 font-bold">3</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">Choices at each step lead to subproblems</h4>
                      <p className="text-slate-400 text-sm">Decision-making problems (include/exclude, buy/sell, cut/don't cut)</p>
                    </div>
                  </div>
                  <div className="flex gap-3 items-start">
                    <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-purple-400 font-bold">4</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">Recursive brute-force seems obvious but too slow</h4>
                      <p className="text-slate-400 text-sm">If naive recursion times out, add memoization to create a <strong>dynamic algorithm</strong></p>
                    </div>
                  </div>
                </div>
              </>
            }
          />
        </div>
      </div>
    </section>
  )
}

interface ArticleCardProps {
  title: string
  icon: React.ReactNode
  content: React.ReactNode
}

function ArticleCard({ title, icon, content }: ArticleCardProps) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden hover:border-slate-600 transition-colors">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-slate-750 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="text-cyan-400">{icon}</div>
          <h3 className="text-xl font-semibold text-white">{title}</h3>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </button>
      
      {isOpen && (
        <div className="px-6 pb-6 text-slate-300 animate-fadeIn">
          {content}
        </div>
      )}
    </div>
  )
}